import { History } from 'history';
import { InlinePRData } from '@nykaa/nes-sitewide-prices-revealed/inlinePriceReveal/types';
import { PageJourney } from 'store/dataLayer/action';
import { getErrorString } from 'store/dataLayer/helpers';
import { getStockString } from '@nykaa/nes-product-card-shared/utils';
import { isValidObject } from '@nykaa/nes-utils/validations';
import {
  pageTypes,
  pageNamePrefix,
  PageRootTypes,
  SiteNavigation,
  siteSubNavigationPrefix,
} from 'shared/constants/dataLayer';
import {
  queryStringObjectFromUrl,
  appendQueryStringToUrl,
} from 'shared/helpers/utils/url';
import { YOUTUBE_EMBED_URL } from 'shared/constants/productPage';
import {
  DESCRIPTION_TAB,
  INGREDIENTS_TAB,
  HOW_TO_USE_TAB,
  MINIMUM_DESCRIPTION_LENGTH,
} from 'shared/constants/productPage';
import {
  Product,
  CategoryLevels,
  Variant,
  PrimaryCategories,
  ManufactureData,
  CohortSaleTemplate,
} from './types';
import { pushEvent } from '@nykaa/nes-data-layer/utils';
import { logger } from '@nykaa/nes-logger';

interface ProductDataLayer {
  categoryLevels: CategoryLevels;
  childId?: string;
  id: string;
  image?: string;
  inStock: boolean;
  stockStatus: string;
  mrp: number;
  name: string;
  offerPrice: number;
  sku: string;
  isLux: number;
  brandName: string;
  currency: string;
  categoryLevel: PrimaryCategories;
  reqSku?: string;
  discount: number;
  rating: number;
  reviewCount: string;
  ratingCount: string;
  packSize?: string;
  isMini?: boolean;
  slug?: string;
  productType?: string;
  displayCombo?: boolean;
  couponsAvailable?: boolean;
  tags?: string[];
  prData?: InlinePRData | null;
  selectedVariantName?: string;
  cohortSaleTemplate?: CohortSaleTemplate | null;
  cohortSaleTemplateV2?: CohortSaleTemplate | null;
}

export interface TabHeaders {
  [key: string]: number;
}

interface TabObject {
  id: string;
  value: string;
}
export interface TabsData {
  [key: number]: TabObject;
}

export const getDataLayerForProduct = (
  productData: Product | null,
  errorCode: number | null,
  params: any = {},
  currentSku: any
) => {
  if (!productData) {
    return {
      pageName: getErrorString(errorCode),
      pageType: pageTypes.PRODUCT,
    };
  }

  const {
    id,
    inStock,
    mrp,
    name,
    offerPrice,
    selectedVariantId,
    sku,
    variants,
    slug,
    parentMedia,
    categoryLevels,
    offers,
    sizeData,
    isLux,
    brandName,
    primaryCategories,
    discount,
    rating,
    reviewCount,
    ratingCount,
    productType,
    displayCombo,
    coupons,
    tags,
    prData,
    hasAplusTrack,
    pdPriceReveal,
    variantType,
    selectedVariantName,
    cohortSaleTemplate,
    social_proof_nudges,
  } = productData;
  const { leftTagName, primaryTagsNames, secondaryTagName } = currentSku;
  let product: ProductDataLayer = {
    categoryLevels,
    id,
    image: parentMedia[0]?.url,
    inStock: inStock,
    stockStatus: getStockString(inStock),
    mrp,
    name,
    offerPrice,
    sku,
    isLux,
    brandName,
    categoryLevel: primaryCategories,
    reqSku: params.skuId,
    discount,
    rating,
    reviewCount,
    ratingCount,
    productType,
    slug,
    displayCombo,
    couponsAvailable: coupons?.length > 0,
    tags,
    prData,
    pdPriceReveal,
    variantType,
    hasAplusTrack,
    selectedVariantName,
    cohortSaleTemplate,
    social_proof_nudges,
    leftTagName,
    primaryTagsNames,
    secondaryTagName,
  };
  let desiredSlug: string = slug;

  // TODO: PI-957 remove this
  // if (selectedVariantId && variants) {
  //   const selectedSKU = variants[selectedVariantId];
  if (selectedVariantId && variants.length > 0) {
    const selectedSKUIndex = variants.findIndex(
      (variant) => variant.childId === selectedVariantId
    );
    const selectedSKU = variants[selectedSKUIndex];

    product = {
      ...product,
      childId: selectedVariantId,
      image: selectedSKU?.media[0]?.url,
      inStock: selectedSKU?.inStock,
      mrp: selectedSKU?.mrp,
      name: selectedSKU?.name,
      offerPrice: selectedSKU?.offerPrice,
      sku: selectedSKU?.sku,
      packSize: selectedSKU?.packSize,
      isMini: selectedSKU?.isMini || false,
      slug: selectedSKU?.slug,
      prData: selectedSKU?.prData,
      cohortSaleTemplate: selectedSKU?.cohortSaleTemplate,
      cohortSaleTemplateV2: selectedSKU?.cohortSaleTemplateV2,
      selectedVariantName: selectedVariantName,
    };
    desiredSlug = selectedSKU?.slug;
  }

  let noDefaultSelection = 'false';
  if (!selectedVariantId && variants.length > 0) {
    noDefaultSelection = 'true';
  }

  const differentialPrice =
    product?.cohortSaleTemplateV2?.price ||
    product?.cohortSaleTemplate?.price ||
    product.offerPrice;
  const differentialDiscount =
    product?.cohortSaleTemplateV2?.discountPercentage ||
    product?.cohortSaleTemplate?.discount ||
    product.discount;
  const differentialPriceTempId =
    product?.cohortSaleTemplateV2?.templateId ||
    product?.cohortSaleTemplate?.templateId ||
    '';

  return {
    pageName: `${pageNamePrefix.PRODUCT}${desiredSlug}`,
    pageType: params.pageType || pageTypes.PRODUCT,
    product,
    hasOffer: offers?.length > 0 ? 'yes' : 'no',
    hasSizeChart: sizeData?.enabled,
    noDefaultSelection,
    analyticsSdkData: {
      pdpProductId: id,
      pdpProductName: name,
    },
    ...(product.prData && { isPdpPriceRevealPresent: true }),
    ...(params.enableDiffPricing &&
      (product.cohortSaleTemplate || product.cohortSaleTemplateV2) && {
        isPdpDifferentialPricing: true,
        differentialPrice,
        differentialDiscount,
        differentialPriceTempId,
      }),
  };
};

export const removeTabCharsFromString = (str = '') => {
  if (str) {
    return str.replace(/\\t/g, '');
  }
  return str;
};

export const stripScriptTags = (str = {}) => {
  const stringifiedStr = JSON.stringify(str);
  return JSON.parse(stringifiedStr.replace(/<script>.*<\/script>/gi, ''));
};

export const removeNewLineCharsFromString = (str = '') => {
  if (str) {
    return str.replace(/\\n/g, '');
  }
  return str;
};

export const getLegalInfoForPDP = (
  data: ManufactureData,
  countryOfManufacture: string,
  countryOfOriginEnabled: boolean = false
) => {
  let legalInfoHtmlString = '';
  const {
    originOfCountryName,
    manufacturerName,
    manufacturerAddress,
    packerName,
    packerAddress,
    importerName,
    importerAddress,
    countryOfOriginLabel,
    manufacturerNameLabel,
    manufacturerAddressLabel,
    importerNameLabel,
    importerAddressLabel,
    packerNameLabel,
    packerAddressLabel,
  } = data;

  // Show country of origin when config flag is enabled
  if (countryOfOriginEnabled && originOfCountryName) {
    const countryName = originOfCountryName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<br><p class="mrp-n-expiry">${countryOfOriginLabel}&nbsp; <p class="content-info">${countryName}</p></p><br>`;
  }

  if (manufacturerName) {
    const name = manufacturerName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${manufacturerNameLabel}&nbsp; <p class="content-info">${name}</p></p>`;
  }
  if (manufacturerAddress) {
    const address = manufacturerAddress.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${manufacturerAddressLabel}&nbsp; <p class="content-info">${address}</p></p><br>`;
  }
  if (countryOfManufacture) {
    const country = countryOfManufacture.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">Country of Manufacture:&nbsp; <p class="content-info">${country}</p></p><br>`;
  }
  if (importerName) {
    const importerNameValue = importerName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${importerNameLabel}&nbsp; <p class="content-info">${importerNameValue}</p></p>`;
  }
  if (importerAddress) {
    const importerAddressValue = importerAddress.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${importerAddressLabel}&nbsp; <p class="content-info">${importerAddressValue}</p></p><br>`;
  }
  if (packerName) {
    const packerNameValue = packerName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${packerNameLabel}&nbsp; <p class="content-info">${packerNameValue}</p></p>`;
  }
  if (packerAddress) {
    const packerAddressValue = packerAddress.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${packerAddressLabel}&nbsp; <p class="content-info">${packerAddressValue}</p></p>`;
  }

  return legalInfoHtmlString;
};

export const extractProductJourneyData = (
  queryString: string,
  id: number
): PageJourney | null => {
  const queryParams = new URLSearchParams(queryString);
  const root = queryParams.get('root');
  const searchItem = queryParams.get('searchItem');
  const recoPosition = queryParams?.get('pps');
  const widgetName = queryParams?.get('widgetName');
  const widgetScroll = queryParams?.get('scrollType');
  const mediaCount = queryParams?.get('sct');
  const journeyData: PageJourney = {};

  switch (root) {
    case PageRootTypes.SEARCH:
      if (searchItem) {
        journeyData.siteNavigation = SiteNavigation.SEARCH;
        try {
          journeyData.siteSubNavigation = `${
            siteSubNavigationPrefix.SEARCH
          }${decodeURI(searchItem)}`;
        } catch (error) {
          logger.error('Error in decoding search term', error);
        }
      }
      break;

    case PageRootTypes.SIMILAR_PRODUCTS_ON_IMAGE:
      journeyData.siteNavigation = SiteNavigation.WIDGETS;
      journeyData.siteSubNavigation = `${siteSubNavigationPrefix.PRODUCT_SIMILAR_PRODUCTS_ON_IMAGE}:${id}:18-${recoPosition}:viewSimilarBottomSheet`;
      break;

    case PageRootTypes.SEARCH_BOX:
      journeyData.siteNavigation = SiteNavigation.SEARCH_BOX;
      journeyData.siteSubNavigation = `${siteSubNavigationPrefix.SEARCH_BOX_RECENTLY_VIEWED}`;
      journeyData.searchBoxValue = `RECENTLY VIEWED PRODUCTS:${id}:${recoPosition}`;
      break;

    case PageRootTypes.SIMILAR_PRODUCTS_PD:
      journeyData.siteNavigation = SiteNavigation.WIDGETS;
      journeyData.siteSubNavigation = `${siteSubNavigationPrefix.PRODUCT_SIMILAR_PRODUCTS}:${id}:${recoPosition}`;
      break;

    case PageRootTypes.SIMILAR_PRODUCTS_PD_HORIZONTAL:
      journeyData.siteNavigation = SiteNavigation.WIDGETS;
      journeyData.siteSubNavigation = `${siteSubNavigationPrefix.PRODUCT_SIMILAR_PRODUCTS_HORIZONTAL}:${id}:${recoPosition}`;
      break;

    case PageRootTypes.PRODUCT_COMPARISON:
      journeyData.siteNavigation = SiteNavigation.WIDGETS;
      journeyData.siteSubNavigation = `${siteSubNavigationPrefix.PRODUCT_COMPARE_PRODUCTS}:${id}:${recoPosition}`;
      break;

    case PageRootTypes.RELATED_SEARCHES:
      journeyData.siteNavigation = SiteNavigation.SEARCH;
      journeyData.siteSubNavigation = siteSubNavigationPrefix.RELATED_SEARCHES;
      break;

    case PageRootTypes.HLP_PD:
      journeyData.siteNavigation = SiteNavigation.WIDGETS;
      if (widgetName && widgetScroll) {
        journeyData.siteSubNavigation = `${SiteNavigation.WIDGETS}:${widgetName}:${widgetScroll}:${siteSubNavigationPrefix.HLP_SUB_NAV}`;
      } else {
        journeyData.siteSubNavigation = `${siteSubNavigationPrefix.PRODUCT_SIMILAR_PRODUCTS_HORIZONTAL}:${siteSubNavigationPrefix.HLP_SUB_NAV}`;
      }
      break;
    case PageRootTypes.FULL_SCREEN_RECO_WIDGET:
      journeyData.siteNavigation = SiteNavigation.WIDGETS;
      journeyData.siteSubNavigation = `${siteSubNavigationPrefix.FULL_SCREEN_RECO_WIDGET}:${id}:${mediaCount}-${recoPosition}:ImageFullScreenView`;
      break;

    default:
      if (widgetName && widgetScroll) {
        journeyData.siteNavigation = SiteNavigation.WIDGETS;
        journeyData.siteSubNavigation = `pdpWidget:${widgetName}:${widgetScroll}:${id}:${recoPosition}`;
      } else {
        return null;
      }
  }

  return journeyData;
};

export const embeddedVideoUrl = (url: string): string => {
  const queryStringObject = queryStringObjectFromUrl(url);
  const videoId = queryStringObject.v;
  return `${YOUTUBE_EMBED_URL}${videoId}`;
};

export const handleSkuIdInUrl = (
  history: History,
  currentSku: Variant,
  parentId: string
) => {
  const { search: queryString, pathname } = history.location;
  const urlParams = new URLSearchParams(queryString);
  const { childId: currentSkuId } = currentSku;
  const QUERY_PARAM_KEY_FOR_SKU = 'skuId';
  const skuId = urlParams.get(QUERY_PARAM_KEY_FOR_SKU);
  let shouldUpdateUrl = false;

  if (skuId) {
    if (skuId !== currentSkuId) {
      urlParams.set(QUERY_PARAM_KEY_FOR_SKU, currentSkuId);
      shouldUpdateUrl = true;
    }
  } else if (parentId !== currentSkuId) {
    // * This will prevent un-necessary updated in case of simple products
    urlParams.append(QUERY_PARAM_KEY_FOR_SKU, currentSkuId);
    shouldUpdateUrl = true;
  }

  if (shouldUpdateUrl) {
    const updatedQueryString = urlParams.toString();
    const updatedProductUrl = appendQueryStringToUrl(
      pathname,
      updatedQueryString
    );
    /**
     * ! MUST USE "history.replace"
     *   if history.push is used, the physical back click will just take the user back to previously selected shade.
     */
    history.replace(updatedProductUrl);
  }
};

export const getTabsValues = (
  description: string,
  ingredients: string,
  howToUse: string
) => {
  let count = -1;
  let tabHeaders: TabHeaders = {};
  let tabsData: TabsData = {};
  if (description && description.length > MINIMUM_DESCRIPTION_LENGTH) {
    count += 1;
    tabHeaders[DESCRIPTION_TAB] = count;
    // id is added bcz on description tab need to add expiry and mrp data also.
    tabsData[count] = { id: DESCRIPTION_TAB, value: description };
  }
  if (ingredients && ingredients.length > MINIMUM_DESCRIPTION_LENGTH) {
    count += 1;
    tabHeaders[INGREDIENTS_TAB] = count;
    tabsData[count] = { id: INGREDIENTS_TAB, value: ingredients };
  }
  if (howToUse && howToUse.length > MINIMUM_DESCRIPTION_LENGTH) {
    count += 1;
    tabHeaders[HOW_TO_USE_TAB] = count;
    tabsData[count] = { id: HOW_TO_USE_TAB, value: howToUse };
  }
  return { tabsData: tabsData, tabHeaders: tabHeaders };
};

export const trackClick = (event) => {
  pushEvent(`${event}_CLICK`, {});
};

export const isProProduct = (proFlag: number, isProUser: number) => {
  if (isProUser && (proFlag === 1 || proFlag === 2)) {
    return true;
  }

  return false;
};

export const getCategoryId = (categoryLevels: CategoryLevels = {}) => {
  const categoryValues = Object.values(categoryLevels);
  const categoryArrayLength = categoryValues?.length;
  const categoryId = categoryValues[categoryArrayLength - 1];
  return categoryId;
};

export const getCatgoryLevelId = (primaryCategories: PrimaryCategories) => {
  if (isValidObject(primaryCategories)) {
    const categoryId = primaryCategories?.l3?.id || '';
    return categoryId;
  }
  return '';
};
export const updateCoupons = (
  coupons: { couponId: number }[],
  payload: { couponId: number; couponStatus: any }[]
) =>
  coupons?.map((oldCoupon) => {
    const correspondingCoupon = payload.find(
      (newCoupon) => newCoupon.couponId === oldCoupon.couponId
    );
    return correspondingCoupon
      ? { ...oldCoupon, couponStatus: correspondingCoupon.couponStatus }
      : oldCoupon;
  });
